'use client'

import { ReactNode, useEffect, useState } from 'react'

import { HEADER_HEIGHT } from '@/app/components/Header'
import { Icon } from '@/app/components/Icon'
import scrollLock from '@/app/hooks/useScrollLock'
import { cn } from '@/app/utils/style'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  classNames?: {
    root?: string
    inner?: string
    backdrop?: string
    closeIcon?: string
  }
  direction?: 'left' | 'right' | 'top' | 'bottom'
  width?: string
  height?: string
  topOffset?: number
  closeOnEscape?: boolean
  variant?: 'slide' | 'centered'
  showBackdrop?: boolean
  showCloseIcon?: boolean
  closeIconSize?: number
  showOnce?: {
    storageKey: string
    storageType: 'localStorage' | 'sessionStorage'
  }
  preserveScrollPosition?: boolean
}

export const Modal = function ({
  isOpen,
  onClose,
  children,
  classNames,
  direction = 'left',
  width = 'w-full',
  height = `h-[calc(100vh-${HEADER_HEIGHT}px)]`,
  topOffset = 0,
  closeOnEscape = true,
  variant = 'slide',
  showBackdrop = false,
  showCloseIcon = false,
  closeIconSize = 25,
  showOnce,
  preserveScrollPosition = false,
}: ModalProps) {
  const [shouldShow, setShouldShow] = useState(true)

  useEffect(() => {
    if (!showOnce) return

    const { storageKey, storageType } = showOnce
    const storage = storageType === 'sessionStorage' ? sessionStorage : localStorage
    const hasShown = storage.getItem(storageKey)

    if (hasShown === 'true') {
      setShouldShow(false)
    } else if (isOpen) {
      storage.setItem(storageKey, 'true')
    }
  }, [showOnce, isOpen])

  useEffect(() => {
    if (shouldShow) {
      scrollLock(isOpen, { preservePosition: preserveScrollPosition })
    }
  }, [isOpen, shouldShow, preserveScrollPosition])

  useEffect(() => {
    if (!closeOnEscape || !shouldShow) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleEscape)
    }

    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, closeOnEscape, onClose, shouldShow])

  useEffect(() => {
    if (!shouldShow || !isOpen) return

    const handleResize = () => {
      scrollLock(true, { preservePosition: preserveScrollPosition })
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [isOpen, shouldShow, preserveScrollPosition])

  if (!shouldShow) {
    return null
  }

  if (variant === 'centered') {
    return (
      <>
        {showBackdrop && (
          <div
            className={cn(
              'fixed inset-0 z-51 bg-modal-backdrop transition-opacity duration-300',
              isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
              classNames?.backdrop,
            )}
            onClick={onClose}
            aria-hidden="true"
          />
        )}

        <div
          className={cn(
            'fixed inset-0 z-52 flex items-center justify-center p-4xl transition-opacity duration-300',
            isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
            classNames?.root,
          )}
          onClick={onClose}
        >
          <div
            className={cn(
              'bg-neutral-100 rounded-2xl p-5xl overflow-y-auto max-h-full relative',
              'transition-transform duration-300',
              isOpen ? 'scale-100' : 'scale-95',
              classNames?.inner,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {showCloseIcon && (
              <button
                onClick={onClose}
                className={cn(
                  'absolute z-2 top-5xl right-5xl mt-[-3px] mr-[-3px] text-neutral-900 hover:text-primary-600 active:text-primary-200 transition-colors cursor-pointer',
                  classNames?.closeIcon,
                )}
                aria-label="Close modal"
                type="button"
              >
                <Icon provider="fontawesome" name="faClose" style="regular" size={closeIconSize} />
              </button>
            )}
            {children}
          </div>
        </div>
      </>
    )
  }

  const directionClasses = {
    left: {
      container: `left-0 top-[${HEADER_HEIGHT}px]`,
      transform: isOpen ? 'translate-x-0' : '-translate-x-full',
      style: undefined,
    },
    right: {
      container: `right-0 top-[${HEADER_HEIGHT}px]`,
      transform: isOpen ? 'translate-x-0' : 'translate-x-full',
      style: undefined,
    },
    top: {
      container: topOffset > 0 ? `inset-x-0` : 'top-0 inset-x-0',
      transform: isOpen ? 'translate-y-0' : '-translate-y-full',
      style: topOffset > 0 ? { top: `${topOffset}px` } : undefined,
    },
    bottom: {
      container: 'bottom-0 inset-x-0',
      transform: isOpen ? 'translate-y-0' : 'translate-y-full',
      style: undefined,
    },
  }

  const config = directionClasses[direction]

  return (
    <div
      className={`${cn(
        'fixed inset-x-0 z-40 lg:hidden transition-transform duration-300 ease-in-out',
        config.container,
        config.transform,
        !isOpen && 'pointer-events-none',
      )} ${classNames?.root || ''}`}
      style={config.style}
    >
      <div className={cn(width, height, 'overflow-y-auto flex flex-col', classNames?.inner)}>
        {children}
      </div>
    </div>
  )
}
